import {
  IonFooter,
  IonHeader,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  type RefresherEventDetail,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { BackButton } from "@/components/back-button";
import { Button } from "@/components/button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { Toolbar } from "@/components/toolbar";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { Routes } from "@/lib/router";
import { useStatusBarHeight } from "@/utils/use-status-bar-height";

export function SettingsContactPage() {
  const { t } = useTranslation();
  const validator = useValidator();
  const statusBarHeight = useStatusBarHeight();

  const { data: organization, refetch } = useQuery({
    queryKey: ["organization"],
    queryFn: () => {
      return extractionWrapper(api.organization.getMyOrganization());
    },
  });

  const sendMessage = (input: { message: string }) => {
    const subject = encodeURIComponent("Contact Support Request");
    const body = encodeURIComponent(input.message);
    const email = organization?.email || "support@heyboomer.nl";
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    return Promise.resolve();
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    event.detail.complete();
  };

  const [form, { Element: FormElement }] = useBoomerForm({
    defaultValues: { message: "" },
    onSubmit: async ({ value }) => await sendMessage(value),
  });

  return (
    <FormElement>
      <IonPage>
        <IonHeader>
          <Toolbar style={{ paddingTop: `calc(${statusBarHeight}px + 0.5rem)` }}>
            <Buttons slot="start">
              <BackButton defaultHref={Routes.Settings()} />
            </Buttons>
            <IonTitle>{t("routes:contact.title")}</IonTitle>
          </Toolbar>
        </IonHeader>
        <Content>
          <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
            <IonRefresherContent refreshingSpinner="circles" />
          </IonRefresher>
          <div className="flex flex-1 flex-col gap-6 pt-4">
            <form.Subscribe
              selector={(state) => [state.isSubmitted] as const}
              children={([isSubmitted]) => (
                <>
                  <p className="font-medium text-txt-secondary">
                    {isSubmitted ? t("routes:contact.text-success") : t("routes:contact.text")}
                  </p>
                  {!isSubmitted && (
                    <form.AppField
                      name="message"
                      validators={{
                        onChange: validator.required({ name: "message" }),
                        onSubmit: validator.required({ name: "message" }),
                      }}
                      children={(field) => (
                        <field.TextareaField
                          required
                          rows={6}
                          withAsterisk={false}
                          withPlaceholder
                        />
                      )}
                    />
                  )}
                </>
              )}
            />
          </div>
        </Content>
        <IonFooter>
          <Toolbar>
            <form.Subscribe
              selector={(state) =>
                [state.canSubmit, state.isSubmitting, state.isSubmitted] as const
              }
              children={([canSubmit, isSubmitting, isSubmitted]) => (
                <Button type="submit" disabled={!canSubmit || isSubmitted} expand="block">
                  {isSubmitted
                    ? t("actions:contact.action-success")
                    : isSubmitting
                      ? t("common:loading")
                      : t("actions:contact.action")}
                </Button>
              )}
            />
          </Toolbar>
        </IonFooter>
      </IonPage>
    </FormElement>
  );
}
